import axios from "axios";

import { isPublicRoute } from "../utils/routes";
import { clearSession } from "../utils/sessionTokens";
import { REFRESH_TOKEN_URL } from "./Service";

const env = process.env.REACT_APP_ENV || "dev";

const DEFAULT_API_BASE_URL = "https://api-practice-uat.kinesin.health";

export const getBaseUrl = (onlyBasePrefix) => {
  if (env === "local") {
    if (onlyBasePrefix) return `http://localhost:8082/`;
    else return `http://localhost:8082/api/practice/v1/`;
  }

  const host = (process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL).replace(
    /\/$/,
    ""
  );
  if (onlyBasePrefix) return `${host}/`;
  return `${host}/api/practice/v1/`;
};

// Singleton promise — prevents multiple simultaneous refresh calls when
// several requests fail with 401 at the same time.
let refreshPromise = null;

// Tracks when the token was last refreshed (proactive refresh every 30 min).
// Module-level so it resets on page load, which is intentional.
let lastProactiveRefresh = Date.now();

const THIRTY_MINUTES = 30 * 60 * 1000;

/*
 * Shared with logoutFromHelper, which used to clear less and so left a dead token in
 * localStorage for App.js to restore on the next load — see clearSession.
 */
const clearTokens = clearSession;

/*
 * Who the 401 belongs to (KIN-2079). The backend used to answer 403 when a request carried no
 * token at all and 401 only when a real token had expired, so "401" meant "this session has
 * run out" and sending the browser to /logout was right. It now answers 401 for both, and the
 * login screen's own unauthenticated calls began forcing a redirect to /logout — which cleared
 * nothing of use, landed back on /login, and went round again.
 *
 * A session that is worth ending is one that had a token to lose. With no token there is nobody
 * to log out, and a public page is already where a logout would send us.
 */
const hasSessionToEnd = () =>
  !!sessionStorage.getItem("accessToken") &&
  !isPublicRoute(window.location.pathname);

async function refreshAccessToken() {
  const refreshToken = sessionStorage.getItem("refreshToken");
  const accessToken = sessionStorage.getItem("accessToken");
  if (!refreshToken) return null;

  try {
    const refreshUrl = `${getBaseUrl(true)}${REFRESH_TOKEN_URL.replace(
      /^\//,
      ""
    )}`;
    const response = await axios.post(
      refreshUrl,
      { refreshToken },
      {
        headers: {
          Accept: "*",
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
      }
    );

    const newAccessToken = response.data.access_token;
    const newRefreshToken = response.data.refresh_token;
    if (!newAccessToken) return null;

    sessionStorage.setItem("accessToken", newAccessToken);
    localStorage.setItem("kinesin-accessToken", newAccessToken);
    if (newRefreshToken) {
      sessionStorage.setItem("refreshToken", newRefreshToken);
      localStorage.setItem("kinesin-refreshToken", newRefreshToken);
    }
    lastProactiveRefresh = Date.now();
    return newAccessToken;
  } catch (error) {
    console.error("Token refresh failed:", error);
    return null;
  }
}

export const createAxiosInstance = (onlyBasePrefix) => {
  const axiosInstance = axios.create({
    baseURL: getBaseUrl(onlyBasePrefix),
    headers: {
      Accept: "*",
    },
    maxContentLength: 50 * 1024 * 1024,
    maxBodyLength: 50 * 1024 * 1024,
  });

  axiosInstance.interceptors.request.use(
    async (config) => {
      // Last-resort guard: never send a request whose URL path contains an
      // "undefined" / "null" segment — that means a missing id was interpolated
      // into the path (e.g. /patient/get/undefined), which 500s on the backend
      // ("cannot convert to Long") and is always a client bug. Block it here and
      // log the URL so the source can be traced. Callers should still guard with
      // React Query `enabled`; this is defence-in-depth.
      if (/(^|\/)(undefined|null)(\/|\?|$)/.test(config.url ?? "")) {
        console.error(
          `[axios] Blocked request with a missing path param: ${config.url}`
        );

        return Promise.reject(
          new Error(`Missing path parameter in request URL: ${config.url}`)
        );
      }

      // Proactively refresh if no refresh has happened in the last 30 minutes
      if (
        Date.now() - lastProactiveRefresh >= THIRTY_MINUTES &&
        sessionStorage.getItem("refreshToken")
      ) {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
          });
        }
        // Best-effort: if this fails we still proceed with the current token
        await refreshPromise.catch(() => {});
      }

      const accessToken = sessionStorage.getItem("accessToken");
      if (accessToken) {
        config.headers["Authorization"] = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      if (
        response.data !== null &&
        typeof response.data === "object" &&
        response.data.status === "success" &&
        Object.prototype.hasOwnProperty.call(response.data, "data")
      ) {
        response.data = response.data.data;
      }

      return response;
    },

    async (error) => {
      const originalRequest = error.config;

      const apiError = error?.response?.data;

      if (apiError?.status === "error" && apiError?.message) {
        error.message = apiError.message;
        if (apiError.code) error.code = apiError.code;
      }

      // Only attempt refresh on 401 (expired token).
      // 403 means forbidden — refreshing won't help, let GlobalLayout handle it.
      if (error?.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const wasSignedIn = hasSessionToEnd();

        // Nothing to refresh and nobody to sign out: let the caller see the 401. This is the
        // login screen's case, and a redirect here is the loop described above.
        if (!wasSignedIn && !sessionStorage.getItem("refreshToken")) {
          return Promise.reject(error);
        }

        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
          });
        }

        const newAccessToken = await refreshPromise;

        if (newAccessToken) {
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        }

        // Refresh failed — clear tokens and force re-login
        clearTokens();
        if (wasSignedIn) window.location.href = "/logout";
      }

      return Promise.reject(error);
    }
  );

  return axiosInstance;
};
